import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { NotificationService } from '../shared-services/notification/notification.service';
import {
  DEFAULT_MSG_KEY,
  defaultErrorMessages,
} from './default-error-message-token';
import { Router } from '@angular/router';

interface ApiErrorDetail {
  message?: string;
  code?: string | number;
}

interface ApiErrorBody {
  errors?: ApiErrorDetail[];
  message?: string;
}

type ApiHttpError = HttpErrorResponse & {
  error?: ApiErrorBody | string;
};

const IDEMPOTENCY_HEADER_NAME = 'Idempotency-Key';
const IDEMPOTENCY_REASON_HEADER = 'error-reason';
const IDEMPOTENCY_ERROR_STATUS_CODES = new Set<number>([409, 422]);

@Injectable()
export class NotifyHttpInterceptorInterceptor implements HttpInterceptor {
  constructor(
    private notifiService: NotificationService,
    private router: Router,
  ) {}

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((err: ApiHttpError) => {
        try {
          if (this.isIdempotencyFailure(request, err)) {
            return throwError(() => err);
          }

          let errorMessage = '';
          if (err.error && err.error.errors) {
            err.error.errors.forEach((error: ApiErrorDetail) => {
              errorMessage += `${error.message} (${error.code})\n`;
            });
          } else {
            errorMessage += this.handleDefaultCase(err, request);
          }

          const route = this.getRoute(errorMessage, err);
          if (route) {
            this.router.navigate([route]);
          }
          this.notifiService.showErrorNotification(
            errorMessage.charAt(0).toUpperCase() + errorMessage.substr(1),
          );
        } catch (e) {
          this.notifiService.showErrorNotification('An error occurred + ' + e);
        }
        return throwError(() => err);
      }),
    );
  }

  private isIdempotencyFailure(
    request: HttpRequest<unknown>,
    err: HttpErrorResponse,
  ): boolean {
    return (
      this.hasIdempotencyStatus(err) &&
      (this.requestHasIdempotencyHeader(request) ||
        this.errorReasonHasIdempotency(err))
    );
  }

  private hasIdempotencyStatus(err: HttpErrorResponse): boolean {
    return IDEMPOTENCY_ERROR_STATUS_CODES.has(err?.status || 0);
  }

  private requestHasIdempotencyHeader(request: HttpRequest<unknown>): boolean {
    return request.headers.has(IDEMPOTENCY_HEADER_NAME);
  }

  private errorReasonHasIdempotency(err: HttpErrorResponse): boolean {
    const reason =
      err?.headers?.get?.(IDEMPOTENCY_REASON_HEADER)?.toLowerCase() || '';
    if (reason.includes('idempotency')) {
      return true;
    }
    const body = typeof err?.error === 'string' ? err.error.toLowerCase() : '';
    return body.includes('idempotency');
  }

  handleDefaultCase(err: ApiHttpError, request: HttpRequest<unknown>): string {
    const defaultMsgKey = request.context?.get(DEFAULT_MSG_KEY);
    if (defaultMsgKey && defaultErrorMessages[defaultMsgKey]) {
      return defaultErrorMessages[defaultMsgKey];
    }
    return err.message;
  }

  getRoute(errorMessage: string, err: ApiHttpError): string | null {
    if (errorMessage.toLowerCase().includes('invalid or expired jwt')) {
      return '/auth-error/EXPJWT9999';
    } else if (err.headers?.get?.('error-reason') === 'CallerIpNotAllowed') {
      return '/auth-error/IPNOTALLOWED';
    } else if (err.headers?.get?.('error-reason') === 'CallerIpBlocked') {
      return '/auth-error/IPBLOCKED';
    }
    return null;
  }
}
